// A decision card on a phone (U10): the highlighted next step as a large button at the bottom of
// the screen for one hand, and (in focus mode) swipe left to archive, right to open the snooze
// picker. The button presses exactly what the card's own button presses: a send only arms, and the
// card's confirm strip (who it goes to, what it says) is brought into view to confirm there.
// Wider screens get the card as it is.
import { useEffect, useRef, type ReactNode } from "react";
import type { CardView } from "@shared/api";
import { isExternal, needsConfirmation } from "@web/feed/actions";
import type { CardController } from "@web/feed/card/useCardController";
import { Button } from "@web/ui/Button";
import { highlightedAction, swipeArchiveAction } from "./swipe";
import { SwipeRow, usePhone } from "./SwipeRow";

function NextStepBar({ card, controller }: { card: CardView; controller: CardController }) {
  const action = highlightedAction(card.actions);
  const armedId = controller.armedAction?.id ?? null;
  const footer = useRef<HTMLDivElement>(null);
  // Armed from here: show the confirm strip, which sits at the end of the card.
  useEffect(() => {
    if (!armedId) return;
    footer.current?.parentElement
      ?.querySelector(`[data-card-id="${CSS.escape(card.id)}"] > footer`)
      ?.scrollIntoView({ block: "end", behavior: "smooth" });
  }, [armedId, card.id]);
  if (!action || armedId || controller.snoozeOpen || !controller.actionable)
    return <div ref={footer} />;
  const blocked = card.updatePending && isExternal(action);
  return (
    <div
      ref={footer}
      data-testid="next-step-bar"
      className="sticky bottom-0 z-10 -mx-1 mt-3 bg-gradient-to-t from-bg via-bg/95 to-transparent px-1 pt-4 pb-[max(0.75rem,env(safe-area-inset-bottom))]"
    >
      <Button
        variant="primary"
        className="h-14 w-full text-base"
        disabled={controller.busy || blocked}
        onClick={() => controller.press(action)}
      >
        {action.label}
        {needsConfirmation(action) ? (
          <span className="text-sm font-normal opacity-80">· then confirm</span>
        ) : null}
      </Button>
    </div>
  );
}

export function PhoneCard({
  card,
  controller,
  active,
  swipe,
  children,
}: {
  card: CardView | undefined;
  controller: CardController;
  active: boolean;
  swipe: boolean;
  children: ReactNode;
}) {
  const phone = usePhone();
  if (!phone || !active || !card) return <>{children}</>;
  const archive = swipeArchiveAction(card.actions);
  const canSwipe = swipe && controller.actionable && !controller.armedAction && !controller.busy;
  return (
    <div>
      <SwipeRow
        enabled={canSwipe}
        className="rounded-xl"
        onArchive={archive && !card.updatePending ? () => controller.press(archive) : null}
        onSnooze={
          card.actions.some((a) => a.behavior === "snooze")
            ? () => controller.setSnoozeOpen(true)
            : null
        }
      >
        {children}
      </SwipeRow>
      <NextStepBar card={card} controller={controller} />
    </div>
  );
}
